"use client";

import { httpsCallable, getFunctions } from "firebase/functions";
import { auth, firebaseApp } from "./firebase";

type RazorpaySuccess = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

type RazorpayOptions = {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill?: { name?: string; email?: string };
  theme?: { color?: string };
  handler: (response: RazorpaySuccess) => void | Promise<void>;
  modal?: { ondismiss?: () => void };
};

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => { open: () => void };
  }
}

const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
export const onlinePaymentsConfigured = viteEnv?.VITE_RAZORPAY_ENABLED === "true"
  && Boolean(viteEnv?.VITE_FIREBASE_APPCHECK_SITE_KEY);

const functions = getFunctions(firebaseApp, "asia-south1");

async function loadRazorpayCheckout() {
  if (window.Razorpay) return;
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-mistry-razorpay="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Checkout failed to load")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.mistryRazorpay = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Checkout failed to load"));
    document.head.appendChild(script);
  });
  if (!window.Razorpay) throw new Error("Checkout is unavailable");
}

export async function startRazorpayCheckout(input: {
  requestId: string;
  mechanicId: string;
  description: string;
}) {
  if (!onlinePaymentsConfigured) throw new Error("Online payments are not activated");
  const user = auth.currentUser;
  if (!user || user.isAnonymous || !user.emailVerified) {
    throw new Error("Verify your account before paying online");
  }
  await loadRazorpayCheckout();
  const createOrder = httpsCallable<
    { requestId: string; mechanicId: string },
    { paymentIntentId: string; orderId: string; amount: number; currency: string; keyId: string }
  >(functions, "createRazorpayOrder");
  const verifyPayment = httpsCallable<
    RazorpaySuccess & { paymentIntentId: string },
    { verified: boolean; paid: boolean; status: string }
  >(functions, "verifyRazorpayPayment");
  const order = (await createOrder({ requestId: input.requestId, mechanicId: input.mechanicId })).data;

  return await new Promise<{ verified: boolean; paid: boolean; status: string }>((resolve, reject) => {
    let completed = false;
    const checkout = new window.Razorpay!({
      key: order.keyId,
      amount: order.amount * 100,
      currency: order.currency,
      name: "Mistry Bhai",
      description: input.description,
      order_id: order.orderId,
      prefill: { name: user.displayName || undefined, email: user.email || undefined },
      theme: { color: "#294a34" },
      handler: async (payment) => {
        try {
          const verified = (await verifyPayment({ ...payment, paymentIntentId: order.paymentIntentId })).data;
          completed = true;
          resolve(verified);
        } catch (error) {
          completed = true;
          reject(error);
        }
      },
      modal: {
        ondismiss: () => {
          if (!completed) reject(new Error("Payment cancelled"));
        },
      },
    });
    checkout.open();
  });
}
